<template>
    <header class="site-navbar" :class="{ scrolled: isScrolled }">
        <div class="navbar-container">
            <!-- Logo -->
            <a class="navbar-brand"
               href="#home"
               aria-label="MMP Concrete LLC"
               @click="closeMenu">
                <img src="@/assets/logo.png"
                     alt="MMP Concrete LLC"
                     class="navbar-logo" />

                <div class="brand-name">
                    <strong>MMP</strong>
                    <span>CONCRETE LLC</span>
                </div>
            </a>

            <!-- Mobile menu button -->
            <button class="menu-button"
                    type="button"
                    :class="{ active: menuOpen }"
                    :aria-expanded="menuOpen"
                    aria-label="Open navigation menu"
                    @click="toggleMenu">
                <span></span>
                <span></span>
                <span></span>
            </button>

            <!-- Navigation -->
            <nav class="navbar-navigation"
                 :class="{ open: menuOpen }"
                 aria-label="Main navigation">
                <a href="#about" @click="closeMenu">
                    {{ t.navigation.about }}
                </a>

                <a href="#services" @click="closeMenu">
                    {{ t.navigation.services }}
                </a>

                <a href="#projects" @click="closeMenu">
                    {{ t.navigation.projects }}
                </a>

                <a href="#process" @click="closeMenu">
                    {{ t.navigation.process }}
                </a>

                <a href="#service-area" @click="closeMenu">
                    {{ t.navigation.area }}
                </a>

                <!-- Language switch -->
                <button class="language-button"
                        type="button"
                        :aria-label="t.language.label"
                        @click="changeLanguage">
                    <span :class="{ selected: language === 'en' }">EN</span>
                    <span class="separator">/</span>
                    <span :class="{ selected: language === 'es' }">ES</span>
                </button>

                <!-- Estimate button -->
                <a class="estimate-button"
                   href="#contact"
                   @click="closeMenu">
                    {{ t.navigation.estimate }}

                    <svg viewBox="0 0 24 24"
                         aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m13 6 6 6-6 6"></path>
                    </svg>
                </a>
            </nav>
        </div>
    </header>
</template>

<script>
export default {
  name: 'SiteNavbar',

  props: {
    t: {
      type: Object,
      required: true
    },

    language: {
      type: String,
      required: true
    }
  },

  emits: ['change-language'],

  data() {
    return {
      menuOpen: false,
      isScrolled: false
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)

    this.handleScroll()
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)

    document.body.classList.remove('menu-open')
  },

  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen
      document.body.classList.toggle('menu-open', this.menuOpen)
    },

    closeMenu() {
      this.menuOpen = false
      document.body.classList.remove('menu-open')
    },

    changeLanguage() {
      this.$emit(
        'change-language',
        this.language === 'en' ? 'es' : 'en'
      )

      this.closeMenu()
    },

    handleScroll() {
      this.isScrolled = window.scrollY > 40
    },

    handleResize() {
      if (window.innerWidth > 980 && this.menuOpen) {
        this.closeMenu()
      }
    }
  }
}
</script>

<style scoped>
    .site-navbar {
        position: fixed;
        z-index: 1000;
        top: 0;
        right: 0;
        left: 0;
        height: var(--navbar-height);
        color: var(--color-white);
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
        transition: background-color 0.3s ease, box-shadow 0.3s ease, height 0.3s ease;
    }

        .site-navbar.scrolled {
            background-color: rgba(21, 21, 19, 0.96);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
            backdrop-filter: blur(12px);
        }

    .navbar-container {
        width: min(100% - 64px, 1440px);
        height: 100%;
        margin-inline: auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 40px;
    }

    .navbar-brand {
        display: inline-flex;
        align-items: center;
        gap: 13px;
        flex-shrink: 0;
    }

    .navbar-logo {
        width: 54px;
        height: 54px;
        object-fit: contain;
        border-radius: 4px;
    }

    .brand-name {
        line-height: 1;
        letter-spacing: 0.08em;
    }

        .brand-name strong {
            display: block;
            font-size: 21px;
            font-weight: 900;
        }

        .brand-name span {
            display: block;
            margin-top: 6px;
            font-size: 9px;
            font-weight: 700;
            letter-spacing: 0.3em;
        }

    .navbar-navigation {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: clamp(15px, 1.7vw, 30px);
    }

        .navbar-navigation > a:not(.estimate-button) {
            position: relative;
            padding-block: 10px;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            white-space: nowrap;
        }

            .navbar-navigation > a:not(.estimate-button)::after {
                position: absolute;
                right: 0;
                bottom: 3px;
                left: 0;
                height: 2px;
                background-color: var(--color-orange);
                content: "";
                transform: scaleX(0);
                transform-origin: right;
                transition: transform 0.25s ease;
            }

            .navbar-navigation > a:not(.estimate-button):hover::after {
                transform: scaleX(1);
                transform-origin: left;
            }

    .language-button {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 8px;
        color: rgba(255, 255, 255, 0.55);
        background: transparent;
        border: 0;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.08em;
    }

        .language-button .selected {
            color: var(--color-orange);
        }

        .language-button .separator {
            color: rgba(255, 255, 255, 0.3);
        }

    .estimate-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 15px 18px;
        color: var(--color-white);
        border: 1px solid rgba(255, 255, 255, 0.65);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        white-space: nowrap;
        transition: color 0.25s ease, background-color 0.25s ease;
    }

        .estimate-button:hover {
            color: var(--color-charcoal);
            background-color: var(--color-white);
        }

        .estimate-button svg {
            width: 17px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.8;
        }

    .menu-button {
        display: none;
        width: 44px;
        height: 44px;
        padding: 10px;
        background: transparent;
        border: 0;
    }

        .menu-button span {
            display: block;
            width: 23px;
            height: 2px;
            margin: 5px auto;
            background-color: var(--color-white);
            transition: transform 0.25s ease, opacity 0.25s ease;
        }

        .menu-button.active span:nth-child(1) {
            transform: translateY(7px) rotate(45deg);
        }

        .menu-button.active span:nth-child(2) {
            opacity: 0;
        }

        .menu-button.active span:nth-child(3) {
            transform: translateY(-7px) rotate(-45deg);
        }

    @media (max-width: 1100px) {
        .brand-name {
            display: none;
        }

        .navbar-container {
            width: min(100% - 40px, 1440px);
        }
    }

    @media (max-width: 980px) {
        .site-navbar {
            background-color: rgba(21, 21, 19, 0.96);
        }

        .navbar-container {
            width: min(100% - 40px, 1440px);
        }

        .navbar-logo {
            width: 48px;
            height: 48px;
        }

        .brand-name {
            display: block;
        }

        .menu-button {
            display: block;
            position: relative;
            z-index: 2;
        }

        .navbar-navigation {
            position: fixed;
            top: var(--navbar-height);
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: stretch;
            justify-content: flex-start;
            gap: 0;
            padding: 35px 25px;
            overflow-y: auto;
            background-color: var(--color-charcoal);
            flex-direction: column;
            opacity: 0;
            pointer-events: none;
            transform: translateY(-15px);
            transition: opacity 0.25s ease, transform 0.25s ease;
        }

            .navbar-navigation.open {
                opacity: 1;
                pointer-events: auto;
                transform: translateY(0);
            }

            .navbar-navigation > a:not(.estimate-button) {
                padding: 17px 4px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                font-size: 13px;
            }

                .navbar-navigation > a:not(.estimate-button)::after {
                    display: none;
                }

        .language-button {
            justify-content: flex-start;
            margin-block: 20px;
            padding: 12px 4px;
            font-size: 13px;
        }

        .estimate-button {
            margin-top: 5px;
            padding: 18px;
            font-size: 11px;
        }
    }

    @media (max-width: 480px) {
        .navbar-container {
            width: calc(100% - 30px);
        }

        .brand-name strong {
            font-size: 18px;
        }

        .brand-name span {
            font-size: 8px;
        }
    }
</style>